import { useEffect, useState, useCallback, useContext } from 'react';
import ViewportContext from '@atlassian/jira-software-deployments-common-table/src/components/table/common/context/viewport/context';
import type { PercentPosition } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/utils/timeline';
import type { Percentage } from '@atlassian/jira-software-deployments-common-table/src/types';
import type { Deployment } from '../../common/types';
import { getFutureIndicatorInfo, getPastIndicatorInfo } from './utils';

export type PastInfo = {
	scrollTo: Percentage;
	left: Percentage;
};
export type FutureInfo = {
	scrollTo: Percentage;
	right: Percentage;
};

export const Directions = {
	LEFT: 'LEFT',
	RIGHT: 'RIGHT',
} as const;
const { LEFT, RIGHT } = Directions;

export type Direction = keyof typeof Directions | null;

export const useNavigateIndicatorInfo = (
	deploymentsSorted: Deployment[],
	getPositionFromDate: (date: Date) => PercentPosition,
): Direction => {
	const { listenToViewportScroll, stopListeningToViewportScroll, requestViewportScrollBounding } =
		useContext(ViewportContext);

	const [indicatorDirection, setIndicatorDirection] = useState<unknown>(null);

	const calculateIndicatorDirection = useCallback(() => {
		if (deploymentsSorted.length === 0) {
			setIndicatorDirection(null);
			return;
		}

		const { scrollLeft, scrollRight, scrollWidth } = requestViewportScrollBounding();

		const past = getPastIndicatorInfo(
			getPositionFromDate(deploymentsSorted[0].lastUpdated).right,
			scrollLeft,
			scrollWidth,
		);
		const future = getFutureIndicatorInfo(
			getPositionFromDate(deploymentsSorted[deploymentsSorted.length - 1].lastUpdated).left,
			scrollRight,
			scrollWidth,
		);

		if (past !== null) {
			if (indicatorDirection !== LEFT) {
				setIndicatorDirection(LEFT);
			}
		} else if (future !== null) {
			if (indicatorDirection !== RIGHT) {
				setIndicatorDirection(RIGHT);
			}
		} else if (indicatorDirection !== null) {
			setIndicatorDirection(null);
		}
	}, [deploymentsSorted, getPositionFromDate, indicatorDirection, requestViewportScrollBounding]);

	useEffect(() => {
		calculateIndicatorDirection();
		listenToViewportScroll(calculateIndicatorDirection);

		return () => {
			stopListeningToViewportScroll(calculateIndicatorDirection);
		};
	}, [listenToViewportScroll, calculateIndicatorDirection, stopListeningToViewportScroll]);

	// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Direction'.
	return indicatorDirection;
};
